<template>
  <div class="panel-grid__block">
    <CompanyEditModal
      v-if="$user.isOwner()"
      ref="companyEditModal"
      v-model:visibility="isCompanyEditModalVisible"
    />
    <CompanyPanelPlaceholder v-if="isLoading" />
    <PxPanel
      v-else-if="company"
      class="profile-panel profile-panel--company px-panel--xs-no-side-borders"
    >
      <div class="profile-headline">
        <div class="profile-photo">
          <PxAvatar
            :name="company.name"
            :photo="company.logo"
            :size="avatarSize"
          />
        </div>
        <div class="profile-headline__text-wrapper">
          <h2 class="profile-headline__title">
            {{ company.name }}
          </h2>
          <h3 class="profile-headline__subtitle">
            {{ companyType }}
            <span
              v-if="company.locations && company.locations.length"
              class="profile-headline__pin-item"
            >
              {{ formattedLocation }}
            </span>
          </h3>
          <PxMatchingInterestTip
            v-if="isMatchingEnabled"
            :id="companyId"
            :name="company.name"
          />
        </div>
        <div class="profile-headline__cta">
          <PxButton
            v-if="$user.isOwner()"
            class="profile-headline__cta--edit-info"
            size="small"
            @click="onClickEdit"
          >
            {{ $t(`profile.company.editButton[${$profile.type()}]`) }}
          </PxButton>
          <PxButton
            v-if="$user.isOwner() && $screen.mdUp"
            v-feature="'teamMembers'"
            class="profile-headline__cta--team-members"
            size="small"
            type="link"
            @click="goToTeamMembersManagementPage"
          >
            {{ $t(`profile.company.editTeamMembers`) }}
          </PxButton>
          <PxMatchingInterest
            v-else-if="showMatchingCta"
            :id="companyId"
            :name="company.name"
            :with-description="false"
          />
          <PxButton
            v-if="canMatch && showAddToListButton"
            class="px-matching-interest__cta--addToList"
            v-bind="addToListButtonProps"
            @click.stop="showListModal"
          />
        </div>
      </div>
      <div class="profile-content">
        <CompanyPanelEntrepreneur
          v-if="$profile.isEntrepreneur()"
          :company="company"
          :user-can-match="canMatch"
          :user-is-connected="isConnected"
          :user-is-interested="isInterested"
          @focus-modal-field="onFocusCompanyModalField"
          @open-company-modal="onOpenCompanyModal"
        />
        <CompanyPanelSupporter
          v-if="$profile.isSupporter()"
          :company="company"
          :user-can-match="canMatch"
          :user-is-connected="isConnected"
          :user-is-interested="isInterested"
          @focus-modal-field="onFocusCompanyModalField"
          @show-modal-view="onShowModalView"
        />
      </div>
    </PxPanel>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import CompanyEditModal from "@/modules/profile/components/company-edit-modal/company-edit-modal.vue";
import CompanyPanelPlaceholder from "@/modules/profile/components/company-panel/company-panel-placeholder.vue";
import CompanyPanelEntrepreneur from "@/modules/profile/components/company-panel/company-panel--entrepreneur.vue";
import CompanyPanelSupporter from "@/modules/profile/components/company-panel/company-panel--supporter.vue";
import PxMatchingInterestTip from "@/components/px-matching-interest/px-matching-interest-tip.vue";

import { ICompany } from "@/modules/profile/services/data/company/company.types";
import { EMatchingInterestActions } from "@/modules/matching/services/store/matching-interest/matching-interest.types";
import { IMatchingInterest } from "@/modules/matching/services/data/matching-interest/matching-interest.interface";

import {
  MATCHING_CONNECTED_INTEREST,
  MATCHING_HAS_INTEREST,
  MATCHING_INITIAL_INTEREST,
  MATCHING_USER_TYPES,
} from "@/modules/matching/constants";

import { locationFormatter } from "@/utils/location-formatter";
import { SupporterTypeState } from "@/modules/supporters/services/store/supporter-type/supporter-type.module";

import {
  EPxButtonSize,
  EPxButtonType,
} from "@/components/px-button/px-button.types";
import { ROUTE_TEAM_MANAGEMENT } from "@/modules/profile/services/router/routes-names";
import { activeModules } from "@/services/utils/utils";

const AVATAR_SIZE_MOBILE = 74;
const AVATAR_SIZE_DESKTOP = 95;

export default defineComponent({
  name: "CompanyPanel",

  components: {
    CompanyEditModal,
    CompanyPanelPlaceholder,
    CompanyPanelEntrepreneur,
    CompanyPanelSupporter,
    PxMatchingInterestTip,
  },

  static: {
    addToListButton: {
      icon: "icon-add-to-list",
      size: EPxButtonSize.EXTRA_SMALL,
      type: EPxButtonType.DARK_BLUE,
    },
  },

  data() {
    return {
      isCompanyEditModalVisible: false,
    };
  },

  computed: {
    showAddToListButton(): boolean {
      if (this.$user.isEntrepreneur()) {
        return (
          this.$screen.lgUp &&
          activeModules().includes("company-lists") &&
          this.$features.isEnabled("companyLists") &&
          this.$features.isEnabled("companyListsForEntrepreneurs")
        );
      } else {
        return (
          this.$screen.lgUp &&
          activeModules().includes("company-lists") &&
          this.$features.isEnabled("companyLists")
        );
      }
    },

    addToListButtonProps(): any {
      return {
        ...this.$options.static.addToListButton,
        label: this.$t("common.components.default.cta.addToList"),
      };
    },
    /**
     * Size of the avatar component.
     */
    avatarSize(): number {
      return this.$screen.smDown ? AVATAR_SIZE_MOBILE : AVATAR_SIZE_DESKTOP;
    },

    company(): ICompany | null {
      return this.$store.state.profile.company.data;
    },

    companyId(): number | null {
      return this.company ? this.company.id : null;
    },

    isSupporterLoading(): boolean {
      return (
        this.$user.isSupporter() &&
        (this.$store.state.profile.supporter.loading ||
          (this.$user.isOwner() && this.$store.state.auth.supporter.loading) ||
          this.$store.getters[SupporterTypeState.Getter.IS_LOADING])
      );
    },

    isLoading(): boolean {
      // console.log("isLoading", this.$store.state.profile.company.loading);
      // console.log("profile loading", this.$store.state.profile.loading);
      // console.log(
      //   "profile company loading",
      //   this.$store.state.profile.company.loading,
      // );
      // console.log(
      //   "matching interest loading",
      //   this.$store.state.matchingInterest.loading,
      // );
      // console.log(activeModules().includes("matching"), activeModules());
      return (
        this.$store.state.auth.company.loading ||
        this.$store.state.profile.loading ||
        this.$store.state.profile.company.loading ||
        (activeModules().includes("matching") &&
          this.$store.state.matchingInterest.loading) ||
        this.isSupporterLoading
      );
    },

    /**
     * Get auth user interests
     */
    authCompany(): ICompany | null {
      return this.$store.state.auth.company.data;
    },

    authCompanyId(): number | null {
      return this.authCompany ? this.authCompany.id : null;
    },

    /**
     * Get auth user interests
     */
    matchingInterests(): Array<IMatchingInterest> {
      return this.$store.state.matchingInterest
        ? this.$store.state.matchingInterest.data
        : [];
    },

    /**
     * Check if store value is set and that it has values
     */
    hasMatchingInterests(): boolean {
      return this.matchingInterests && !!this.matchingInterests.length;
    },

    /**
     * Get auth user type
     */
    userType(): string {
      return MATCHING_USER_TYPES[this.$user.getUserAccountType()];
    },

    /**
     * Get target interest user type
     */
    targetType(): string {
      const oppositeUserType = +!this.$user.getUserAccountType();
      return MATCHING_USER_TYPES[oppositeUserType];
    },

    /**
     * Return current interest state if available
     */
    currentMatchingInterest(): IMatchingInterest | null {
      if (this.hasMatchingInterests) {
        return (
          this.matchingInterests.find(
            (interest: IMatchingInterest) =>
              interest[this.userType] === this.authCompanyId &&
              interest[this.targetType] === this.companyId,
          ) || null
        );
      }

      return null;
    },

    /**
     * Return final interest between both parties
     */
    interestState(): number {
      return this.currentMatchingInterest
        ? this.currentMatchingInterest.state_of_interest
        : MATCHING_INITIAL_INTEREST;
    },

    isConnected(): boolean {
      return this.interestState === MATCHING_CONNECTED_INTEREST;
    },

    isInterested(): boolean {
      return this.interestState === MATCHING_HAS_INTEREST;
    },

    canMatch(): boolean {
      return this.$store.state.matchingInterest
        ? this.$store.state.matchingInterest.canMatch
        : false;
    },

    formattedLocation(): string {
      const locationComponent =
        this.company && this.company.locations && this.company.locations[0]
          ? this.company.locations[0]
          : null;

      return locationFormatter(locationComponent);
    },

    isMatchingEnabled(): boolean {
      return (
        this.$features.isEnabled("match") &&
        activeModules().includes("matching")
      );
    },

    showMatchingCta(): boolean {
      return this.canMatch && this.isMatchingEnabled;
    },

    companyType(): string {
      return this.$t(`common.userTypes[${this.company?.type}]`);
    },
  },

  async created() {
    // If  store not filled, fetch interests
    if (!this.hasMatchingInterests && this.isMatchingEnabled) {
      this.fetchAuthUserInterests();
    }

    // If supporter profile, loading supporter types
    if (this.$user.isSupporter()) {
      await this.$store.dispatch(SupporterTypeState.Action.GET_VALUES);
    }
  },

  methods: {
    onClickEdit() {
      this.isCompanyEditModalVisible = true;
    },

    onOpenCompanyModal() {
      this.isCompanyEditModalVisible = true;
    },

    async onFocusCompanyModalField(field: string) {
      this.isCompanyEditModalVisible = true;
      await this.$nextTick();
      (this.$refs.companyEditModal as any).focusInputField(field);
    },

    async onShowModalView(view: string) {
      this.isCompanyEditModalVisible = true;
      await this.$nextTick();
      (this.$refs.companyEditModal as any).setCurrentView(view);
    },

    fetchAuthUserInterests() {
      this.$store.dispatch(EMatchingInterestActions.FETCH);
    },

    // TODO: improve the emission of this event to not be repeated in two components
    showListModal() {
      this.$emit("show-modal");
    },

    goToTeamMembersManagementPage() {
      this.$router.push({ name: ROUTE_TEAM_MANAGEMENT });
    },
  },
});
</script>

<style lang="scss" scoped>
.profile-panel--company {
  min-width: 320px;

  @include breakpoint-up(md) {
    min-width: 650px;
  }
}

.profile-panel--company :deep() .px-panel__body {
  padding: 18px 14px;

  @include breakpoint-up(md) {
    padding: 21px 25px 29px;
  }
}

.profile-headline__text-wrapper {
  position: relative;
  top: -1px;

  &:not(:first-child) {
    padding-left: 16px;
  }

  &:not(:last-child) {
    padding-right: 16px;
  }
}

.profile-content__cta {
  @include breakpoint-down(sm) {
    padding-top: 16px;
  }
}

.profile-headline {
  &__cta--edit-info {
    display: flex;
  }

  &__cta--team-members.el-button {
    float: right;

    :deep() span {
      font-size: 14px;
    }
  }
}

.profile-headline__cta {
  :deep() .el-button--default span,
  :deep() .el-button--medium span {
    font-size: 12px;
  }

  :deep() .px-button .px-icon + div {
    margin-left: 3px;
  }

  @include breakpoint-down(sm) {
    width: 100%;
  }
}

.profile-headline__cta > .el-button {
  @include breakpoint-down(sm) {
    width: 100%;
  }
}

.profile-headline__cta :deep() {
  .px-matching-interest {
    @include breakpoint-down(sm) {
      align-items: flex-start;
    }
  }

  .px-matching-interest__cta {
    min-width: 182px;
    height: 32px;

    @include breakpoint-down(sm) {
      min-width: 290px;
    }
  }

  .px-matching-interest__cta--addToList {
    float: right;
    height: 32px;
    margin-top: 8px;

    .px-icon + div {
      margin-left: 8px;
    }

    @include breakpoint-down(sm) {
      float: left;
    }
  }
}

.profile-photo {
  margin-top: -31px;
  background-color: white;

  @include breakpoint-up(md) {
    margin-top: -36px;
  }
}

.profile-content {
  margin-top: 22px;

  @include breakpoint-up(md) {
    margin-top: 31px;
  }
}
</style>
